<div class="wrapper wrapper-content animated fadeInRight">

  <!-- Type Configuration Panel -->
  <div class="row">
    <div class="col-lg-12">
      <div class="ibox">
        <div class="ibox-title">
          <h5><i class="fa fa-sliders"></i> Webhook Type Configuration</h5>
          <div class="ibox-tools">
            <a class="collapse-link"><i class="fa fa-chevron-up"></i></a>
          </div>
        </div>
        <div class="ibox-content">
          <p class="text-muted small m-b-md">
            Control which webhook types are active. Disabled types will ignore integration events and not send webhooks, regardless of subscriptions.
          </p>

          @if (typeConfigsLoading() && typeConfigurations().length === 0) {
            <div class="text-center p-sm">
              <i class="fa fa-spinner fa-spin text-muted"></i>
              <span class="text-muted m-l-sm">Loading type configurations...</span>
            </div>
          }

          @if (typeConfigurations().length > 0) {
            <div class="row">
              @for (config of typeConfigurations(); track config.type) {
                <div class="col-lg-3 col-md-4 col-sm-6">
                  <div class="widget p-sm"
                       [ngClass]="{'border-left-primary': config.isEnabled, 'border-left-default': !config.isEnabled}"
                       style="border-left: 3px solid; margin-bottom: 10px; background: #fafafa; border-radius: 3px;">
                    <div class="d-flex" style="display: flex; justify-content: space-between; align-items: center;">
                      <div>
                        <h5 class="m-b-xs" style="margin: 0;">
                          {{ getWebhookTypeLabel(config.type) }}
                        </h5>
                        <p class="text-muted small m-b-none" style="margin: 2px 0 0;">
                          {{ config.description }}
                        </p>
                      </div>
                      <div style="flex-shrink: 0; margin-left: 12px;">
                        <button class="btn btn-xs"
                                [ngClass]="config.isEnabled ? 'btn-primary' : 'btn-default'"
                                [disabled]="togglingType() !== null"
                                (click)="toggleType(config)"
                                [title]="config.isEnabled ? 'Click to disable' : 'Click to enable'">
                          @if (togglingType() === config.type) {
                            <i class="fa fa-spinner fa-spin"></i>
                          } @else if (config.isEnabled) {
                            <i class="fa fa-check"></i> Enabled
                          } @else {
                            <i class="fa fa-times"></i> Disabled
                          }
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              }
            </div>
          }
        </div>
      </div>
    </div>
  </div>

  <!-- Header Row -->
  <div class="row">
    <div class="col-lg-12">
      <div class="ibox">
        <div class="ibox-title">
          <h5><i class="fa fa-plug"></i> Webhook Subscriptions</h5>
          <div class="ibox-tools">
            <span class="label label-info m-r-sm">{{ activeCount() }} active</span>
            <span class="label label-default m-r-sm">{{ totalCount() }} total</span>
            <button class="btn btn-primary btn-xs" (click)="openCreateModal()">
              <i class="fa fa-plus"></i> New Subscription
            </button>
            <button class="btn btn-white btn-xs m-l-xs" (click)="loadSubscriptions()" [disabled]="loading()">
              <i class="fa fa-refresh" [ngClass]="{'fa-spin': loading()}"></i> Refresh
            </button>
          </div>
        </div>
        <div class="ibox-content">

          <!-- Error Alert -->
          @if (error()) {
            <div class="alert alert-danger">
              <i class="fa fa-exclamation-triangle"></i>
              {{ error() }}
              <button class="btn btn-xs btn-default m-l-sm" (click)="loadSubscriptions()">
                Retry
              </button>
            </div>
          }

          <!-- Filters -->
          <div class="row m-b-md">
            <div class="col-sm-4">
              <label class="control-label">Filter by Type</label>
              <select class="form-control input-sm"
                      (change)="onTypeFilterChange($any($event.target).value)">
                <option value="all">All Types</option>
                @for (type of webhookTypes; track type) {
                  <option [value]="type">{{ getWebhookTypeLabel(type) }}</option>
                }
              </select>
            </div>
          </div>

          <!-- Loading -->
          @if (loading() && filteredSubscriptions().length === 0) {
            <div class="text-center p-lg">
              <i class="fa fa-spinner fa-spin fa-2x text-muted"></i>
              <p class="text-muted m-t-sm">Loading subscriptions...</p>
            </div>
          }

          <!-- Empty State -->
          @if (!loading() && !error() && filteredSubscriptions().length === 0) {
            <div class="text-center p-lg">
              <i class="fa fa-plug fa-3x text-muted"></i>
              <h4 class="text-muted m-t-md">No webhook subscriptions found</h4>
              <p class="text-muted">Click "New Subscription" to create one.</p>
            </div>
          }

          <!-- Subscriptions Table -->
          @if (filteredSubscriptions().length > 0) {
            <div class="table-responsive">
              <table class="table table-striped table-hover">
                <thead>
                  <tr>
                    <th>Client Name</th>
                    <th>Type</th>
                    <th>Endpoint URL</th>
                    <th class="text-center">Status</th>
                    <th>Created</th>
                    <th>Last Modified</th>
                    <th class="text-center">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  @for (sub of filteredSubscriptions(); track sub.id) {
                    <tr>
                      <td>
                        <strong>{{ sub.clientName }}</strong>
                      </td>
                      <td>
                        <span class="label label-info">{{ getWebhookTypeLabel(sub.type) }}</span>
                        <div class="small text-muted m-t-xs">{{ getWebhookTypeDescription(sub.type) }}</div>
                      </td>
                      <td>
                        <code class="small">{{ sub.endpointUrl }}</code>
                      </td>
                      <td class="text-center">
                        @if (sub.isActive) {
                          <span class="label label-primary">Active</span>
                        } @else {
                          <span class="label label-default">Inactive</span>
                        }
                      </td>
                      <td class="small text-muted">{{ formatDate(sub.createdOnUtc) }}</td>
                      <td class="small text-muted">{{ formatDate(sub.modifiedOnUtc) }}</td>
                      <td class="text-center">
                        <button class="btn btn-white btn-xs m-r-xs"
                                title="Test"
                                (click)="testSubscription(sub)"
                                [disabled]="testingSubscriptionId() !== null">
                          @if (testingSubscriptionId() === sub.id) {
                            <i class="fa fa-spinner fa-spin"></i>
                          } @else {
                            <i class="fa fa-bolt"></i>
                          }
                        </button>
                        <button class="btn btn-white btn-xs m-r-xs"
                                title="Edit"
                                (click)="openEditModal(sub)">
                          <i class="fa fa-pencil"></i>
                        </button>
                        <button class="btn btn-danger btn-xs"
                                title="Delete"
                                (click)="deleteSubscription(sub)">
                          <i class="fa fa-trash"></i>
                        </button>
                      </td>
                    </tr>
                  }
                </tbody>
              </table>
            </div>
          }

          <!-- Test Result Panel -->
          @if (testResult()) {
            <div class="m-t-md">
              <div class="ibox">
                <div class="ibox-title" style="border-top: 2px solid #1ab394;">
                  <h5>
                    <i class="fa fa-bolt"></i> Test Result
                    @if (testResultSubscription(); as sub) {
                      — {{ sub.clientName }}
                    }
                  </h5>
                  <div class="ibox-tools">
                    <button class="btn btn-xs btn-white" (click)="dismissTestResult()">
                      <i class="fa fa-times"></i> Dismiss
                    </button>
                  </div>
                </div>
                <div class="ibox-content">
                  <div class="row">
                    <div class="col-sm-3">
                      <strong>Status</strong>
                      <div class="m-t-xs">
                        @if (testResult()!.success) {
                          <span class="label label-primary"><i class="fa fa-check"></i> Success</span>
                        } @else {
                          <span class="label label-danger"><i class="fa fa-times"></i> Failed</span>
                        }
                      </div>
                    </div>
                    <div class="col-sm-3">
                      <strong>HTTP Status</strong>
                      <div class="m-t-xs">
                        @if (testResult()!.statusCode !== null) {
                          <span class="label" [ngClass]="{
                            'label-primary': testResult()!.statusCode! >= 200 && testResult()!.statusCode! < 300,
                            'label-warning': testResult()!.statusCode! >= 300 && testResult()!.statusCode! < 500,
                            'label-danger': testResult()!.statusCode! >= 500
                          }">{{ testResult()!.statusCode }}</span>
                        } @else {
                          <span class="text-muted">N/A</span>
                        }
                      </div>
                    </div>
                    <div class="col-sm-3">
                      <strong>Response Time</strong>
                      <div class="m-t-xs">{{ testResult()!.elapsedMs }}ms</div>
                    </div>
                    @if (testResult()!.errorMessage) {
                      <div class="col-sm-3">
                        <strong>Error</strong>
                        <div class="m-t-xs text-danger">{{ testResult()!.errorMessage }}</div>
                      </div>
                    }
                  </div>
                  @if (testResult()!.responseBody) {
                    <div class="m-t-md">
                      <strong>Response Body</strong>
                      <pre class="m-t-xs" style="max-height: 200px; overflow-y: auto; font-size: 12px;">{{ testResult()!.responseBody }}</pre>
                    </div>
                  }
                </div>
              </div>
            </div>
          }

        </div>
      </div>
    </div>
  </div>
</div>

<!-- Create / Edit Modal -->
@if (showModal()) {
  <div class="modal-backdrop fade in"></div>
  <div class="modal fade in" style="display: block;" tabindex="-1" role="dialog">
    <div class="modal-dialog" role="document">
      <div class="modal-content">
        <div class="modal-header">
          <button type="button" class="close" (click)="closeModal()">
            <span>&times;</span>
          </button>
          <h4 class="modal-title">
            <i class="fa fa-plug"></i>
            {{ isEditing() ? 'Edit' : 'New' }} Webhook Subscription
          </h4>
        </div>
        <div class="modal-body">
          <form (ngSubmit)="saveSubscription()">

            <!-- Client Name -->
            <div class="form-group" [ngClass]="{'has-error': formErrors()['clientName']}">
              <label class="control-label">Client Name <span class="text-danger">*</span></label>
              <input type="text"
                     class="form-control"
                     placeholder="e.g. LPGA Website"
                     [ngModel]="formData().clientName"
                     (ngModelChange)="updateFormField('clientName', $event)"
                     name="clientName"
                     required>
              @if (formErrors()['clientName']) {
                <span class="help-block">{{ formErrors()['clientName'] }}</span>
              }
            </div>

            <!-- Webhook Type (only for create) -->
            @if (!isEditing()) {
              <div class="form-group">
                <label class="control-label">Webhook Type <span class="text-danger">*</span></label>
                <select class="form-control"
                        [ngModel]="formData().webhookType"
                        (ngModelChange)="updateFormField('webhookType', $event)"
                        name="webhookType">
                  @for (type of webhookTypes; track type) {
                    <option [ngValue]="type">{{ getWebhookTypeLabel(type) }}</option>
                  }
                </select>
                <span class="help-block small text-muted">
                  Cannot be changed after creation.
                </span>
                <div class="m-t-sm p-sm" style="background: #f5f5f5; border-radius: 4px;">
                  <div class="small"><strong>{{ getWebhookTypeDescription(formData().webhookType) }}</strong></div>
                  <div class="small text-muted m-t-xs">Triggered by:</div>
                  <ul class="list-unstyled small m-b-none m-t-xs">
                    @for (event of getWebhookTypeTriggerEvents(formData().webhookType); track event) {
                      <li><i class="fa fa-bolt text-info m-r-xs"></i>{{ event }}</li>
                    }
                  </ul>
                </div>
              </div>
            } @else {
              <div class="form-group">
                <label class="control-label">Webhook Type</label>
                <p class="form-control-static">
                  <span class="label label-info">{{ getWebhookTypeLabel(formData().webhookType) }}</span>
                </p>
                <div class="m-t-xs p-sm" style="background: #f5f5f5; border-radius: 4px;">
                  <div class="small"><strong>{{ getWebhookTypeDescription(formData().webhookType) }}</strong></div>
                  <div class="small text-muted m-t-xs">Triggered by:</div>
                  <ul class="list-unstyled small m-b-none m-t-xs">
                    @for (event of getWebhookTypeTriggerEvents(formData().webhookType); track event) {
                      <li><i class="fa fa-bolt text-info m-r-xs"></i>{{ event }}</li>
                    }
                  </ul>
                </div>
              </div>
            }

            <!-- Endpoint URL -->
            <div class="form-group" [ngClass]="{'has-error': formErrors()['endpointUrl']}">
              <label class="control-label">Endpoint URL <span class="text-danger">*</span></label>
              <input type="url"
                     class="form-control"
                     placeholder="https://example.com/webhook"
                     [ngModel]="formData().endpointUrl"
                     (ngModelChange)="updateFormField('endpointUrl', $event)"
                     name="endpointUrl"
                     required>
              @if (formErrors()['endpointUrl']) {
                <span class="help-block">{{ formErrors()['endpointUrl'] }}</span>
              }
              <span class="help-block small text-muted">
                Must be a valid HTTP or HTTPS URL where webhook payloads will be delivered.
              </span>
            </div>

            <!-- Secret Key -->
            <div class="form-group" [ngClass]="{'has-error': formErrors()['secretKey']}">
              <label class="control-label">Secret Key <span class="text-danger">*</span></label>
              <input type="password"
                     class="form-control"
                     placeholder="Enter secret key for HMAC signature"
                     [ngModel]="formData().secretKey"
                     (ngModelChange)="updateFormField('secretKey', $event)"
                     name="secretKey"
                     required>
              @if (formErrors()['secretKey']) {
                <span class="help-block">{{ formErrors()['secretKey'] }}</span>
              }
              <span class="help-block small text-muted">
                Used to sign webhook payloads. Not displayed after creation.
                @if (isEditing()) {
                  <strong>Enter the new secret key to update it.</strong>
                }
              </span>
            </div>

          </form>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-white" (click)="closeModal()" [disabled]="saving()">
            Cancel
          </button>
          <button type="button" class="btn btn-primary" (click)="saveSubscription()" [disabled]="saving()">
            @if (saving()) {
              <i class="fa fa-spinner fa-spin"></i> Saving...
            } @else {
              <i class="fa fa-check"></i> {{ isEditing() ? 'Update' : 'Create' }}
            }
          </button>
        </div>
      </div>
    </div>
  </div>
}
